'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import ImgResized from '../Widgets/ImgResized.react';

import { getLoggedServingsOfRecipe } from '../../utils/Meals';
import { roundForHumans, smartCeil } from '../../utils/Math';
import MealStore from '../../stores/MealStore';
import SmartFraction from '../Widgets/SmartFraction.react';
import FoodUnitsSelector from '../DailyLog/FoodUnitsSelectorModal.react';
import Popup from '../Widgets/Popup.react';

import './HowMuchToEat.scss';

export default class HowMuchToEat extends Component {

    static propTypes = {
       uuid: PropTypes.string,
       meal:  PropTypes.object,
       recipe: PropTypes.object,
       participants: PropTypes.array,
       scaling: PropTypes.number,
       factor: PropTypes.number,
       profile: PropTypes.object,
       onRemoveParticipant: PropTypes.func,
       surplus: PropTypes.bool,
       isLeftover: PropTypes.bool,
       dishNumber: PropTypes.number,
       onChangePortionAmount: PropTypes.func,
       leftoverServings: PropTypes.number,

       width: PropTypes.number,
       height: PropTypes.number,
    };

    static defaultProps = {
        width: 500,
        height: 500,
        factor: 1,
        leftoverServings: 0,
    }

    static contextTypes = {
        viewportWidth: PropTypes.number,
        isPro: PropTypes.bool,
    };

    constructor(props) {
        super(props);

        const thetaStart = 2 * Math.PI * Math.random();
        const meals = MealStore.getMeals();

        const meal = props.meal;

        this.state = {
            thetaStart, // start theta off somewhere random
            meals,
            meal,

            slices: this.getRecipePortionSlices(
                props.profile, props.participants, props.recipe, props.scaling, props.factor, props.width, props.height, thetaStart, props.uuid, meals, props.leftoverServings, meal
            ),

            // Hover tooltip state
            showTooltip: false,
            tooltipContent: '',
            tooltipPosition: { x: 0, y: 0 }
        };
    }

    componentDidMount = () => {
        MealStore.addChangeListener(this.onMealStoreChange);
    }

    onMealStoreChange = () => {
        const meals = MealStore.getMeals();
        this.setState({meals});
    }

    UNSAFE_componentWillReceiveProps = (nextProps) => {
        this.setState({slices: this.getRecipePortionSlices(
            nextProps.profile, nextProps.participants, nextProps.recipe, nextProps.scaling, nextProps.factor,
            nextProps.width, nextProps.height, this.state.thetaStart, nextProps.uuid, this.state.meals, nextProps.leftoverServings, nextProps.meal
        )});
    }

    getRecipePortionSlices = (profile, participants, recipe, scaling, factor, width, height, thetaStart, uuid, meals, leftoverServings, meal) => {
        if (!participants || !recipe) {
            return null;
        }

        const servings = recipe.servings * factor * (scaling || 1);
        const isLeftoverMeal = meal && meal.meal_type === 'leftover';

        const colors = [
            {slice: '#be3b8b', text: '#ffffff'},
            {slice: '#0f8ebf', text: '#ffffff'},
            {slice: '#0b9669', text: '#ffffff'},
            {slice: '#ff7735', text: '#ffffff'},
            {slice: '#FFA579', text: '#ffffff'},
            {slice: '#62EBA7', text: '#ffffff'},
            {slice: '#EB959E', text: '#ffffff'},
            {slice: '#B4DEFF', text: '#ffffff'},
            {slice: '#F8D692', text: '#ffffff'},
            //{slice: '#000000', text: '#ffffff'},
        ];

        let portions = [];

        // First, add the patient a single serving
        const cx = width / 2;
        const cy = height / 2;
        const radius = width / 2 * 1.00;
        const tradius = radius * 0.75; // text position radius
        let theta = thetaStart;
        let needed = 0;
        let portion_unit = profile.units_mode === 'metric' ? 'g' : 'oz';
        const roundUpToNearestServing = false;
        const loggedServings = getLoggedServingsOfRecipe(meal, recipe, roundUpToNearestServing);

        const validParticipants = participants.filter(m => m && (m.uuid || m.uuid === ""));

        // First pass: calculate total needed servings
        validParticipants.forEach((member) => {
            const isUser = member.uuid === 'USER';
            const portionValue = member.portion ?? 1;
            needed += isUser && loggedServings ? loggedServings : portionValue;
        });

        // For leftover meals: use participant servings as denominator (pie chart = 100%)
        // For original meals: use total yield as denominator
        const denominator = isLeftoverMeal ? needed : servings;
        const step = 1 / denominator * 2 * Math.PI;

        // Second pass: create slices
        validParticipants.forEach((member, index) => {
            // Check if this is the primary user - can be 'USER' or the actual profile.uuid
            // MealDetails passes profile.uuid while other contexts may pass 'USER'
            const isUser = member.uuid === 'USER' || member.uuid === profile.uuid;
            const portionValue = member.portion ?? 1; // Default to 1 if portion is undefined
            const memberPortion = isUser && loggedServings ? loggedServings : portionValue;

            // Does this profile use metric or english (and do we have a calculated mass)?
            let portion_size = null;

            if (profile.units_mode === 'metric' && recipe.grams_per_serving) {
                portion_size = memberPortion * recipe.grams_per_serving
            }  else if (recipe.grams_per_serving) {
                portion_size = (memberPortion * recipe.grams_per_serving) / 28.3495;
            }

            let portion = {
                name: isUser ? (profile.first_name || profile.name) : (member.name || 'Family Member'),
                portion_size: portion_size,
                portion_unit,

                circle: step * memberPortion >= 2 * Math.PI,

                rx: radius,
                ry: radius,

                // Long arc, if the arc is greater than 180deg, then the arc is "long"
                la: step * memberPortion > Math.PI ? 1 : 0,

                // Arc's circle center X and Y
                cx: cx,
                cy: cy,

                // Arc start position
                sx: cx + radius * Math.cos(theta),
                sy: cy + radius * Math.sin(theta),

                // Arc end position
                ex: cx + radius * Math.cos(theta + step * memberPortion),
                ey: cy + radius * Math.sin(theta + step * memberPortion),

                // Text position
                tx: cx + tradius * Math.cos(theta + step / 2 * memberPortion),
                ty: cy + tradius * Math.sin(theta + step / 2 * memberPortion) + 10,

                color: colors[portions.length % colors.length],
                text: Math.round(memberPortion / denominator * 100) + '%',
                numerator: memberPortion,
                denominator: denominator,
                member,
            };

            portions.push(portion);

            theta += step * memberPortion;
        });

        const remaining = servings - needed;
        const assignedLeftovers = leftoverServings || 0;
        const surplusServings = remaining - assignedLeftovers;

        const noLeftovers = (meals.filter(meal => meal.parent_uuid === uuid) || []).length == 0;
        const noLeftoverTag = recipe.tags.includes("No Leftovers");

        // Add leftover slice if there are assigned leftovers (skip for leftover meals)
        if (assignedLeftovers > 0 && !isLeftoverMeal) {
            let leftover_size = profile.units_mode === 'metric'
                             ? assignedLeftovers * recipe.grams_per_serving
                             : (assignedLeftovers * recipe.grams_per_serving) / 28.3495;

            portions.push({
                name: "Leftovers",
                portion_size: leftover_size,
                portion_unit,

                circle: assignedLeftovers * step >= 2 * Math.PI,

                rx: radius,
                ry: radius,

                la: (step * assignedLeftovers) > Math.PI ? 1 : 0,

                cx: cx,
                cy: cy,

                sx: cx + radius * Math.cos(theta),
                sy: cy + radius * Math.sin(theta),

                ex: cx + radius * Math.cos(theta + step * assignedLeftovers),
                ey: cy + radius * Math.sin(theta + step * assignedLeftovers),

                tx: cx + tradius * Math.cos(theta + step * assignedLeftovers / 2),
                ty: cy + tradius * Math.sin(theta + step * assignedLeftovers / 2) + 10,

                color: {slice: '#a8acbe', text: '#ffffff'},
                text: Math.round(assignedLeftovers / servings * 100) + '%',
                amount: assignedLeftovers / servings,
                numerator: assignedLeftovers,
                denominator: servings,
            });

            theta += step * assignedLeftovers;
        }

        // Add surplus slice if there are unassigned servings (skip for leftover meals)
        if (surplusServings > 0 && !isLeftoverMeal) {
            // Use "Food Surplus" unless "No Leftovers" tag is present
            let surplusName = "Food Surplus";
            if (noLeftoverTag && noLeftovers) {
                surplusName = "Remaining Servings";
            }

            let surplus_size = profile.units_mode === 'metric'
                             ? surplusServings * recipe.grams_per_serving
                             : (surplusServings * recipe.grams_per_serving) / 28.3495;

            portions.push({
                name: surplusName,
                portion_size: surplus_size,
                portion_unit,

                circle: surplusServings * step >= 2 * Math.PI,

                rx: radius,
                ry: radius,

                la: (step * surplusServings) > Math.PI ? 1 : 0,

                cx: cx,
                cy: cy,

                sx: cx + radius * Math.cos(theta),
                sy: cy + radius * Math.sin(theta),

                ex: cx + radius * Math.cos(theta + step * surplusServings),
                ey: cy + radius * Math.sin(theta + step * surplusServings),

                tx: cx + tradius * Math.cos(theta + step * surplusServings / 2),
                ty: cy + tradius * Math.sin(theta + step * surplusServings / 2) + 10,

                color: {slice: '#d4d4d4', text: '#666666'},
                text: Math.round(surplusServings / servings * 100) + '%',
                amount: surplusServings / servings,
                numerator: surplusServings,
                denominator: servings,
            });
        }

        return portions;
    }

    onChangePortionAmount = (logged_portion, logged_unit, logged_amount, logged_grams, logged_milliliters, unit) => {
        const {onChangePortionAmount, meal} = this.props;
        onChangePortionAmount(meal, logged_portion, logged_unit, logged_amount, logged_grams, logged_milliliters)
    }

    showTooltip = (event, slice, index) => {
        const { profile } = this.props;
        const tooltipContent = `${slice.name} ${slice.numerator}/${slice.denominator} of total yield · ${slice.portion_size ? `${roundForHumans(slice.portion_size)}${slice.portion_unit} ` : ''}${slice.numerator ? `(${slice.numerator} serving${slice.numerator !== 1 ? 's' : ''})` : ''}`;
        
        this.setState({
            showTooltip: true,
            tooltipContent,
            tooltipPosition: {
                x: event.clientX,
                y: event.clientY
            }
        });
    }

    hideTooltip = () => {
        this.setState({
            showTooltip: false
        });
    }

    renderSmartFraction = (numerator, denominator) => {
        // Add safety checks for undefined values
        if (numerator === undefined || denominator === undefined) {
            console.error('ERROR: renderSmartFraction called with undefined values:', { numerator, denominator });
            return <span>0/0</span>;
        }

        if (numerator.toString().length < 3 && denominator.toString().length < 3) {
            return <SmartFraction unStyle={true} numerator={numerator} denominator={denominator}/>
        }

        const value = smartCeil(numerator/denominator) > 0 ? smartCeil(numerator/denominator) : numerator/denominator;

        return <SmartFraction unStyle={true} value={value}/>
    }

    render = () => {
        const { recipe, onRemoveParticipant, children, onClick, openEditMealServingsModal, surplus, profile, isLeftover, dishNumber, onChangePortionAmount } = this.props;
        const { slices, meal } = this.state;
        const { viewportWidth, isPro } = this.context;

        const portion_unit = profile.units_mode === 'metric' ? 'g' : 'oz';

        // For leftover meals: only show amount participants are eating
        // For original meals: show total yield (all slices)
        const totalAmount = isLeftover
            ? slices
                .filter(s => s.member) // Only participant slices (have member property)
                .reduce((total, member) => total + member.portion_size, 0)
            : slices.reduce((total, member) => total + member.portion_size, 0);

        if (!slices || !recipe.servings) {
            return <span />
        }

        const hasLeftovers = slices.filter(e => e.name === 'Leftovers').length > 0
        const hasSurplus = slices.filter(e => e.name === 'Food Surplus' || e.name === 'Remaining Servings').length > 0

        const disclaimerTextLeftover = `* Following food safety guidelines, it is best to consume cooked food and leftovers between 3-4 days. 
                                It is recommended that anything consumed after these guidelines be frozen until to ready to eat`;

        const disclaimerTextSurplus = `** Food Surplus: Depending on our recipes, the number of servings, and specific portion sizes for who is eating, it is possible to have a surplus of food after leftovers are assigned to family members. This food surplus serving can used as needed to reduce food waste.`

        return (
            <section className="how-much-to-eat">
                <div className="chart">
                    <div style={{position: 'relative'}}>
                        <svg viewBox="0 0 500 500">
                        {slices.map((p, i) => {
                            // Use circle when slice is full OR when there's only one slice
                            // (avoids white separator lines for near-100% portions like 0.9999)
                            if (p.circle || slices.length === 1) {
                                return (
                                    <g key={i}>
                                        <circle
                                            cx={p.cx}
                                            cy={p.cy}
                                            r={p.rx}
                                            stroke="white"
                                            strokeWidth="10"
                                            fill={p.color.slice}
                                            style={{cursor: 'pointer'}}
                                            onMouseEnter={(e) => this.showTooltip(e, p, i)}
                                            onMouseLeave={() => this.hideTooltip()}
                                        />
                                        <text textAnchor="middle" x={p.cx} y={p.cy + 10} fill={p.color.text} style={{pointerEvents: 'none'}}>{p.text}</text>
                                    </g>
                                );
                            }

                            return (
                                <g key={i}>
                                    <path
                                        d={`M ${p.sx} ${p.sy} A ${p.rx} ${p.rx} 0 ${p.la} 1 ${p.ex} ${p.ey} L ${p.cx} ${p.cy}`}
                                        stroke="white"
                                        strokeWidth="10"
                                        fill={p.color.slice}
                                        strokeLinecap="round"
                                        style={{cursor: 'pointer'}}
                                        onMouseEnter={(e) => this.showTooltip(e, p, i)}
                                        onMouseLeave={() => this.hideTooltip()}
                                    />
                                    <path
                                        d={`M ${p.cx} ${p.cy} L ${p.sx} ${p.sy}`}
                                        stroke="white"
                                        strokeWidth="10"
                                        fill="none"
                                        strokeLinecap="round"
                                        style={{cursor: 'pointer'}}
                                        onMouseEnter={(e) => this.showTooltip(e, p, i)}
                                        onMouseLeave={() => this.hideTooltip()}
                                    />
                                    <text textAnchor="middle" x={p.tx} y={p.ty} fill={p.color.text} style={{pointerEvents: 'none'}}>{p.text}</text>
                                </g>
                            );
                        })}
                        </svg>


                        {this.state.showTooltip && (
                            <div
                                className="pie-slice-tooltip-hover"
                                style={{
                                    position: 'fixed',
                                    left: this.state.tooltipPosition.x,
                                    top: this.state.tooltipPosition.y - 10,
                                    transform: 'translate(-50%, -100%)',
                                    padding: '17px 20px 10px 10px',
                                    fontSize: '14px',
                                    whiteSpace: 'nowrap',
                                    zIndex: 1000,
                                    pointerEvents: 'none'
                                }}
                            >
                                {this.state.tooltipContent}
                            </div>
                        )}
                    </div>
                    {recipe.grams_per_serving ?
                        <>
                            <p><em>{isLeftover ? "Total Leftover Amount: " : "Total Yield: "}</em> {`${roundForHumans(totalAmount)}${portion_unit}`}</p>
                            <p><em>1 Serving = </em>{`${roundForHumans(profile.units_mode === 'metric' ? recipe.grams_per_serving : recipe.grams_per_serving / 28.3495)}${portion_unit}`}</p>
                        </>
                    : null}
                </div>

                <ul className="portion-key">
                    <li>{isLeftover ? "leftover portion sizes for:" : "portion sizes for:"}</li>
                    <li>{dishNumber ? <span className="dish-number">{`DISH ${dishNumber}: `}</span> : null}<em>{recipe.title}</em></li>
                    {slices.map((p, i) => {
                        const isUser = profile.uuid && p?.member?.uuid === 'USER';
                        return (
                            <li key={i} data-removable={p.member && onRemoveParticipant ? true : false} className="participant-item"
                                >
                                <div className="key-color" style={{ backgroundColor: p.color.slice }}></div>
                                <div className="participant-info">
                                    <span className="member-name">{p.name}</span>
                                    <span className="slice-info">
                                        {" • "}
                                        {p.numerator != 1 || p.denominator != 1 ? this.renderSmartFraction(p.numerator, p.denominator) : null}
                                        {p.numerator != 1 || p.denominator != 1 ? ` of total ${p.portion_size ? "(" : ""}` : `Full amount ${p.portion_size ? "(" : ""}`}
                                        {p.portion_size ? <span>{roundForHumans(p.portion_size)}{p.portion_unit}</span> : null}
                                        {p.portion_size ? ")" : null}
                                        {isUser && p.member ? (
                                            <Popup 
                                                positionClassName={viewportWidth < 750 ? "el-popup-bottom-right" : "el-popup-bottom-center"}
                                                className="el-popup-dark portion-info-popup el-fonts"
                                                button={<i className="feather feather-info" />}
                                            >
                                                <p>Your portion size is set to {p.member.portion || 1} serving{(p.member.portion || 1) !== 1 ? 's' : ''} to help you best meet your daily nutrition goals. The recipe has been scaled so everyone in your household gets the right amount, with leftovers accounted for.</p>
                                            </Popup>
                                        ) : null}
                                        {p.member && onRemoveParticipant ? <i className="icon-trash-can2" title={`Remove ${p.name} from this meal`}/> : null}
                                        {(p.name == "Food Surplus" || p.name == "Remaining Servings") ? "**" : ""}
                                        {p.name == "Leftovers" ? "*" : ""}
                                        {p.name == "Leftovers" && onClick ? <button className="modify-leftovers-btn" onClick={onClick}>modify leftovers</button> : null}
                                        {isUser && meal?.logged_amount && (!isPro || meal.id) && p.portion_size ? <FoodUnitsSelector buttonText="change amount" profile={profile} food={recipe} meal={meal} onChangeAmount={onChangePortionAmount ? this.onChangePortionAmount : null} /> : null}
                                    </span>
                                    {p.member && <div className="portion-size-text">
                                        Portion size: {p.numerator} {p.numerator === 1 ? 'serving' : 'servings'}
                                    </div>}
                                </div>
                            </li>
                        );
                    })}
                    {children ? <li>{children}</li> : null}
                    {onClick && (
                        <button className="el-medium-btn el-wide-btn el-grayish-blue-outline-btn change-whos-eating-btn" onClick={onClick}>change who's eating</button>
                    )}
                    {(hasLeftovers || isLeftover) && viewportWidth >= 750 ?
                        <p className="leftovers-disclaimer">{disclaimerTextLeftover}</p>
                    : null}
                    {hasSurplus && viewportWidth >= 750 ?
                        <p className="surplus-disclaimer">{disclaimerTextSurplus}</p>
                    : null}

                </ul>
                {(hasLeftovers || isLeftover) && viewportWidth < 750 ?
                    <p className="leftovers-disclaimer">{disclaimerTextLeftover}</p>
                : null}
                {hasSurplus && viewportWidth < 750 ?
                    <p className="surplus-disclaimer">{disclaimerTextSurplus}</p>
                : null}

            </section>
        );
    }
}
